import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import { Control, DivIcon, FeatureGroup, GeoJSON, LatLng, latLng, LatLngBounds, Layer, LayerGroup, Map, MapOptions, Marker, MarkerOptions, tileLayer, TileLayer } from 'leaflet';
import proj4 from 'proj4';
import { Subject, Subscription } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { Organisation } from '../../interfaces/routering/organisation';
import { ASSIGNED_GRID_BORDERS, DEFAULT_GRID_BORDERS, HOVER_GRID_BORDERS, SELECTED_GRID_BORDERS } from '../../../@fury/utils/leaflet';
import { HttpClient } from '@angular/common/http';
import { OverviewService } from '../overview/overview.service';
import { centroid } from '@turf/turf';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { User } from '../../interfaces/routering/user';
import { Tile } from '../../interfaces/routering/tile';
import Layers = Control.Layers;
import Scale = Control.Scale;

@Injectable()

export class LeafletMapService {

  public mapLayerStandard: TileLayer = tileLayer('https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/standaard/EPSG:3857/{z}/{x}/{y}.png', {
    detectRetina: true,
    attribution: 'Kaartgegevens &copy; <a href="https://www.kadaster.nl">Kadaster</a>',
    minZoom: 6,
    maxZoom: 20,
  });

  public mapLayerSatellite: TileLayer = tileLayer('https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0/Actueel_orthoHR/EPSG:3857/{z}/{x}/{y}.png', {
    detectRetina: true,
    attribution: 'Kaartgegevens &copy; <a href="https://www.kadaster.nl">Kadaster</a>',
    minZoom: 6,
    maxZoom: 20,
  });

  public zoom: number = 16;
  public center: LatLng = latLng(52.08486, 4.26783);

  public mapOptions: MapOptions = {
    layers: [this.mapLayerStandard],
    zoom: this.zoom,
    center: this.center,
  };

  public baseMaps = {
    'Achtergrond kaart': this.mapLayerStandard,
    'Luchtfoto kaart': this.mapLayerSatellite
  };

  // noinspection JSUnusedGlobalSymbols
  private toggleControl = L.Control.extend({
    onAdd: (): HTMLDivElement => {
      const container: HTMLDivElement = L.DomUtil.create('div', 'layer-toggle leaflet-bar');

      const cityBtn: HTMLAnchorElement = L.DomUtil.create('a', 'layer-toggle-btn', container) as HTMLAnchorElement;
      cityBtn.href = '#';
      cityBtn.title = 'Toggle City Borders';
      cityBtn.innerHTML = '<span class="material-icons-outlined">flag</span>';

      const gridBtn: HTMLAnchorElement = L.DomUtil.create('a', 'layer-toggle-btn', container) as HTMLAnchorElement;
      gridBtn.href = '#';
      gridBtn.title = 'Toggle Grid';
      gridBtn.innerHTML = '<span class="material-icons-outlined">border_all</span>';

      const labelBtn: HTMLAnchorElement = L.DomUtil.create('a', 'layer-toggle-btn', container) as HTMLAnchorElement;
      labelBtn.href = '#';
      labelBtn.title = 'Toggle Labels';
      labelBtn.innerHTML = '<span class="material-icons-outlined">label</span>';

      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);

      const setActive = (el: HTMLElement, active: boolean): void => {
        el.classList.toggle('is-active', active);
      };

      const toggleLayer = (layer?: L.Layer, btn?: HTMLElement): void => {
        if (!this.mapContainer || !layer || !btn) {
          return;
        }

        const isOn: boolean = this.mapContainer.hasLayer(layer);
        if (isOn) {
          this.mapContainer.removeLayer(layer);
        } else {
          this.mapContainer.addLayer(layer);
        }

        setActive(btn, !isOn);
      };


      setActive(cityBtn, true);
      setActive(gridBtn, true);
//        setActive(labelBtn, true);

      L.DomEvent.on(cityBtn, 'click', (e: Event): void => {
        L.DomEvent.preventDefault(e);
        toggleLayer(this.borderLayer, cityBtn);
      });

      L.DomEvent.on(gridBtn, 'click', (e: Event): void => {
        L.DomEvent.preventDefault(e);
        toggleLayer(this.gridLayer, gridBtn);
      });

      L.DomEvent.on(labelBtn, 'click', (e: Event): void => {
        L.DomEvent.preventDefault(e);

        toggleLayer(this.gridLabelLayer, labelBtn);
      });

      return container;
    },
  });

  public mapContainer: Map = null;
  private markerPane = null;
  private markerLayer: FeatureGroup = new FeatureGroup();
  private borderPane = null;
  private borderLayer: GeoJSON = null;
  private gridLayer: GeoJSON = null;
  private gridLabelLayer: LayerGroup = new LayerGroup();
  private gridPane = null;
  public singleMarker: Marker = null;

  private defaultZoomed: number = 13;

  public zoomChanged$: Subject<number> = new Subject<number>();

  private assignedGridIds = new Set<string | number>();
  private user$: Subscription = null;

  constructor(
    private http: HttpClient,
    private overviewService: OverviewService,
    private authenticationService: AuthenticationService,
  ) {
  }

  init(mapContainer: Map): void {
    this.mapContainer = mapContainer;

    if (mapContainer !== null) {
      this.addControls();
      this.addLayers();

      this.authenticationService.organisation$.subscribe((organisation: Organisation): void => {
        if (organisation.center_lat && organisation.center_lng && organisation.center_zoom) {
          setTimeout((): void => {
            if (this.mapContainer !== null) {
              this.mapContainer.setView([organisation.center_lat, organisation.center_lng], organisation.center_zoom);
            }
          }, 100);
          this.zoom = organisation.center_zoom;
          this.center = latLng(organisation.center_lat, organisation.center_lng);
        }
      });

      this.subscribeToAssignedGrids();

      setTimeout((): void => {
        if (this.mapContainer !== null) {
          this.mapContainer.invalidateSize();
        }
      }, 100);
    }

    this.mapContainer.on('zoomend', (): void => this.zoomChanged$.next(this.mapContainer.getZoom()));
  }

  private addControls(): void {
    this.mapContainer.addControl(new this.toggleControl({position: 'topleft'}));
    this.mapContainer.addControl(new Layers(this.baseMaps));
    this.mapContainer.addControl(new Scale({maxWidth: 200, metric: true, imperial: false, updateWhenIdle: true, position: 'bottomright'}));
  }

  private addLayers(): void {
    // add layer for grid tooltips
    this.gridLabelLayer.addTo(this.mapContainer);

    // add layer for city borders
    this.loadCityBordersGeoJson('assets/geojsons/cityborders.geojson');

    // add layer for grid
    this.overviewService.tiles().subscribe((response: any): void => this.addGeoJsonGridLayer(response));

    // add layer for marker
    if (!this.markerPane) {
      this.markerPane = this.mapContainer.createPane('markerPane').style.zIndex = '650';
      this.markerLayer = L.geoJSON(null, {
        pane: 'markerPane'
      }).addTo(this.mapContainer);
    }
  }

  private loadCityBordersGeoJson(url: string): void {
    this.http.get<any>(url).subscribe({
      next: (geojson) => this.mapContainer.whenReady(() => this.addGeoJsonBorderLayer(geojson)),
      error: (err): void => console.error('Failed to load GeoJSON:', err),
    });
  }

  private addGeoJsonBorderLayer(geojson: any): void {
    if (!this.mapContainer) {
      return;
    }

    if (this.borderLayer) {
      this.borderLayer.removeFrom(this.mapContainer);
    }

    if (!this.borderPane) {
      this.borderPane = this.mapContainer.createPane('borderPane').style.zIndex = '610';
    }

    this.borderLayer = L.geoJSON(geojson, {
      pane: 'borderPane',
      style: () => DEFAULT_GRID_BORDERS,
    }).addTo(this.mapContainer);
  }

  private addGeoJsonGridLayer(geojson: any): void {
    if (!this.mapContainer) {
      return;
    }

    if (!geojson) {
      return;
    }

    if (!this.gridPane) {
      this.gridPane = this.mapContainer.createPane('gridPane').style.zIndex = '620';
    }

    if (this.gridLabelLayer) {
      this.gridLabelLayer.clearLayers();
      this.gridLabelLayer.removeFrom(this.mapContainer);
    }

    if (this.gridLayer) {
      this.gridLayer.clearLayers();
      this.gridLayer.removeFrom(this.mapContainer);
    }

    this.gridLayer = L.geoJSON(geojson, {
      pane: 'gridPane',
      style: (feature) => (this.isAssigned(feature) ? ASSIGNED_GRID_BORDERS : DEFAULT_GRID_BORDERS),
      onEachFeature: (feature, layer): void => {
        const polygonLayer = layer as L.Path;

        layer.on('mouseover', (): void => {
          this.applyGridStyle(polygonLayer, feature, HOVER_GRID_BORDERS);
        });

        layer.on('mouseout', (): void => {
          this.applyGridStyle(polygonLayer, feature, DEFAULT_GRID_BORDERS);
        });

        const label: string = feature?.properties?.label ?? feature?.properties?.label;
        if (label) {
          const centerFeature = centroid(feature);
          const [lng, lat] = centerFeature.geometry.coordinates;

          const marker = L.marker([lat, lng], {
            interactive: false,
            opacity: 0,
          });

          marker.bindTooltip((): string => {
              return String(label);
            },
            {
              permanent: true,
              direction: 'center',
              className: 'grid-label',
              opacity: 0.95,
              offset: [-15, 28]
            });

          marker.addTo(this.gridLabelLayer);
        }
      }
    }).addTo(this.mapContainer);

    this.mapContainer.removeLayer(this.gridLabelLayer);
  }

  private subscribeToAssignedGrids(): void {
    this.user$ = this.authenticationService.user$
      .pipe(
        map((user: User): number[] => user?.tiles.map((tile: Tile): number => tile.id) ?? []),
        distinctUntilChanged((a: number[], b: number[]): boolean => a.length === b.length && a.every((id: number, i: number): boolean => id === b[i])),
      )
      .subscribe((ids: number[]): void => {
        this.assignedGridIds = new Set(ids);
        this.refreshGridStyles();
      });
  }

  private refreshGridStyles(): void {
    if (!this.gridLayer) {
      return;
    }
    this.gridLayer.eachLayer((layer: Layer): void => {
      const polygonLayer: L.Path = layer as L.Path;
      const feature = (layer as any).feature;

      this.applyGridStyle(polygonLayer, feature, DEFAULT_GRID_BORDERS);
    });
  }

  private isAssigned(feature: any): boolean {
    return this.assignedGridIds.has(feature?.properties?.id);
  }

  private applyGridStyle(layer: L.Path, feature: any, requestedStyle: L.PathOptions): void {
    if (this.isAssigned(feature) && requestedStyle !== SELECTED_GRID_BORDERS) {
      layer.setStyle(ASSIGNED_GRID_BORDERS);
      return;
    }
    layer.setStyle(requestedStyle);
  }

  marker(iconColor: string = null): MarkerOptions {
    return {
      icon: new DivIcon({
        html: '<div class="leaflet-custom-marker-pin"' + (iconColor ? 'style="color:' + iconColor + '"' : '') + '></div>',
        iconSize: [24, 36],
        iconAnchor: [12, 36],
      }),
      draggable: false,
    };
  }

  updateMarker(coords: [number, number], zoom: number = null): void {
    if (this.singleMarker) {
      this.singleMarker.setLatLng({lat: coords[0], lng: coords[1]});
    } else {
      this.singleMarker = new Marker({lat: coords[0], lng: coords[1]}, this.marker('single'));
      this.markerLayer.addLayer(this.singleMarker);
    }

    if (this.mapContainer !== null && this.mapContainer !== undefined) {
      this.mapContainer.whenReady(() => this.mapContainer.setView(coords, zoom ?? this.defaultZoomed));
    }
  }

  clear(clearContainer: boolean = false): void {
    this.markerLayer.clearLayers();
    this.singleMarker = null;
    if (document.querySelector('.legend-toggle') !== null) {
      document.querySelector('.legend-toggle').removeAllListeners('click');
    }

    if (clearContainer && this.mapContainer !== null) {
      this.mapContainer = null;
    }

    if (this.user$ !== null) {
      this.user$.unsubscribe();
    }
  }

  convertLatLngToNewRd(coords: [number, number]): [number, number] {
    proj4.defs('EPSG:28992',
      '+proj=sterea +lat_0=52.1561605555556 +lon_0=5.38763888888889 ' +
      '+k=0.9999079 +x_0=155000 +y_0=463000 +ellps=bessel ' +
      '+towgs84=565.4171,50.3319,465.5524,-0.398957,0.343988,-1.8774,4.0725 ' +
      '+units=m +no_defs +type=crs'
    );
    return proj4('EPSG:4326', 'EPSG:28992', coords);
  }

  convertNewRdToLatLng(coords: [number, number]): [number, number] {
    proj4.defs('EPSG:28992',
      '+proj=sterea +lat_0=52.1561605555556 +lon_0=5.38763888888889 ' +
      '+k=0.9999079 +x_0=155000 +y_0=463000 +ellps=bessel ' +
      '+towgs84=565.4171,50.3319,465.5524,-0.398957,0.343988,-1.8774,4.0725 ' +
      '+units=m +no_defs +type=crs'
    );
    return proj4('EPSG:28992', 'EPSG:4326', coords);
  }
}
